import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';

// Once it shows, the loading screen stays at least this long, so entering the studio never flashes
export const MIN_LOADING_MS = 600;

const StudioReady = createContext(() => {});
export const StudioReadyProvider = StudioReady.Provider;

/**
 * Whether the studio loading screen shows: from entering the studio (from the site, a sign-in or a
 * link) until the account has loaded and the first page says its data is in, and for at least
 * MIN_LOADING_MS. Moving between studio pages afterwards doesn't show it again; leaving the studio
 * means the next visit does. Returns { loading, markReady } for the frame and its pages.
 */
export const useStudioEntrance = (inStudio) => {
  const { loading: accountLoading } = useAuth();
  const [entered, setEntered] = useState(false);
  const [pageReady, setPageReady] = useState(false);
  const [minShown, setMinShown] = useState(false);

  useEffect(() => {
    if (!inStudio) {
      setEntered(false);
      setPageReady(false);
      setMinShown(false);
      return undefined;
    }
    if (entered) return undefined;
    const timer = setTimeout(() => setMinShown(true), MIN_LOADING_MS);
    return () => clearTimeout(timer);
  }, [inStudio, entered]);

  useEffect(() => {
    if (inStudio && !entered && !accountLoading && pageReady && minShown) setEntered(true);
  }, [inStudio, entered, accountLoading, pageReady, minShown]);

  const markReady = useCallback(() => setPageReady(true), []);

  return { loading: inStudio && !entered, markReady };
};

/**
 * A studio page tells the loading screen its first data is in (or failed, so its error can show).
 * Outside the studio frame it does nothing.
 */
export const useStudioReady = (ready) => {
  const markReady = useContext(StudioReady);
  useEffect(() => {
    if (ready) markReady();
  }, [ready, markReady]);
};
